import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ShieldAlert,
  Languages,
  History,
  CheckCircle2,
  CheckCircle,
  ArrowRight,
  Download,
  Settings as SettingsIcon,
  ChevronRight,
  Save,
  RotateCcw,
  Sliders,
  Eye,
  Mail,
  Send,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import { Button, Card, Input } from '@lms/ui';
import { apiClient } from '../lib/api-client';
import { useProgramsOverview } from '../hooks/usePrograms';
import { Bilingual } from '../components/Bilingual';
import { copy } from '../copy/copy';
import { useLanguage } from '../lib/LanguageContext';
import { useBrandingStore, DEFAULT_BRAND_SETTINGS } from '../store/branding';
import { useNotificationStore } from '../store/notifications';
import { MailReadinessCard } from '../components/MailReadinessCard';

export function Settings() {
  const { data: overview } = useProgramsOverview();
  const { language, t } = useLanguage();
  const {
    settings: brandSettings,
    updateSettings: updateBrandSettings,
    resetSettings: resetBrandSettings,
  } = useBrandingStore();
  const { addNotification } = useNotificationStore();

  // Branding Form State
  const [prevBrandSettings, setPrevBrandSettings] = useState(brandSettings);
  const [formTitleId, setFormTitleId] = useState(brandSettings.titleId);
  const [formTitleAr, setFormTitleAr] = useState(brandSettings.titleAr);
  const [formSubtitleId, setFormSubtitleId] = useState(brandSettings.subtitleId);
  const [formSubtitleAr, setFormSubtitleAr] = useState(brandSettings.subtitleAr);
  const [previewLang, setPreviewLang] = useState<'id' | 'ar'>('id');

  // Continuous Mailketing status query (auto poll every 60s)
  const {
    data: liveMailStatus,
    isLoading: isCheckingMail,
    isFetching: isFetchingMail,
    refetch: refetchMail,
    error: mailStatusError,
  } = useQuery({
    queryKey: ['mail', 'status'],
    queryFn: () => apiClient.mail.getStatus(),
    refetchInterval: 60000,
    staleTime: 30000,
  });

  const [testEmailTo, setTestEmailTo] = useState('');
  const [testRecipientName, setTestRecipientName] = useState('');
  const [testTemplate, setTestTemplate] = useState<
    | 'RESET_PASSWORD'
    | 'NEW_MATERIAL'
    | 'PENDING_EVALUATION'
    | 'INACTIVITY_REMINDER'
    | 'REGISTRATION_APPROVED'
    | 'REGISTRATION_SUBMITTED'
  >('REGISTRATION_SUBMITTED');
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false);
  const [lastTestResult, setLastTestResult] = useState<{
    success: boolean;
    to: string;
    template: string;
    messageId?: string;
    response?: string;
    error?: string;
    timestamp: Date;
  } | null>(null);

  const handleCheckMail = async () => {
    try {
      const res = await refetchMail();
      if (res.error) throw res.error;
      if (res.data?.status === 'CONNECTED') {
        addNotification({
          type: 'success',
          title: t(copy.settings.mailConnectedTitle),
          message: res.data?.details?.message || t(copy.settings.mailConnectedFallback),
        });
      } else {
        addNotification({
          type: 'error',
          title: t(copy.settings.mailDisconnectedTitle),
          message: res.data?.details?.message || t(copy.settings.mailDisconnectedFallback),
        });
      }
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Gagal menghubungi server';
      addNotification({
        type: 'error',
        title: t(copy.settings.mailCheckFailedTitle),
        message: msg,
      });
    }
  };

  const handleSendTestEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!testEmailTo.trim()) {
      addNotification({
        type: 'error',
        title: 'Validasi Gagal',
        message: 'Masukkan alamat email penerima.',
      });
      return;
    }

    setIsSendingTestEmail(true);
    try {
      const res = await apiClient.mail.sendTest({
        to: testEmailTo.trim(),
        template: testTemplate,
        recipientName: testRecipientName.trim() || undefined,
      });
      setLastTestResult({
        success: true,
        to: testEmailTo.trim(),
        template: testTemplate,
        messageId: res?.messageId,
        response: res?.response,
        timestamp: new Date(),
      });
      addNotification({
        type: 'success',
        title: t(copy.settings.testEmailSuccess),
        message:
          res?.message ||
          `Email uji coba template [${testTemplate}] berhasil dikirim ke ${testEmailTo}`,
      });
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Gagal mengirim email uji coba';
      setLastTestResult({
        success: false,
        to: testEmailTo.trim(),
        template: testTemplate,
        error: msg,
        timestamp: new Date(),
      });
      addNotification({
        type: 'error',
        title: 'Pengiriman Gagal',
        message: msg,
      });
    } finally {
      setIsSendingTestEmail(false);
    }
  };

  // Sync form state if store settings change externally
  if (
    brandSettings.titleId !== prevBrandSettings.titleId ||
    brandSettings.titleAr !== prevBrandSettings.titleAr ||
    brandSettings.subtitleId !== prevBrandSettings.subtitleId ||
    brandSettings.subtitleAr !== prevBrandSettings.subtitleAr
  ) {
    setPrevBrandSettings(brandSettings);
    setFormTitleId(brandSettings.titleId);
    setFormTitleAr(brandSettings.titleAr);
    setFormSubtitleId(brandSettings.subtitleId);
    setFormSubtitleAr(brandSettings.subtitleAr);
  }

  const handleSaveBranding = (e: React.FormEvent) => {
    e.preventDefault();
    updateBrandSettings({
      titleId: formTitleId.trim() || DEFAULT_BRAND_SETTINGS.titleId,
      titleAr: formTitleAr.trim() || DEFAULT_BRAND_SETTINGS.titleAr,
      subtitleId: formSubtitleId.trim() || DEFAULT_BRAND_SETTINGS.subtitleId,
      subtitleAr: formSubtitleAr.trim() || DEFAULT_BRAND_SETTINGS.subtitleAr,
    });
    addNotification({
      type: 'success',
      title: t(copy.settings.toastBrandingSaved),
      message: 'Perubahan judul dan sub-judul portal sidebar telah diterapkan secara langsung.',
    });
  };

  const handleResetBranding = () => {
    resetBrandSettings();
    setFormTitleId(DEFAULT_BRAND_SETTINGS.titleId);
    setFormTitleAr(DEFAULT_BRAND_SETTINGS.titleAr);
    setFormSubtitleId(DEFAULT_BRAND_SETTINGS.subtitleId);
    setFormSubtitleAr(DEFAULT_BRAND_SETTINGS.subtitleAr);
    addNotification({
      type: 'info',
      title: t(copy.settings.toastBrandingReset),
      message: 'Judul dan sub-judul dikembalikan ke default Tarbiyah Panel.',
    });
  };

  const auditLogs =
    overview?.activities.slice(0, 5).map(a => ({
      id: a.id,
      time: new Date(a.timestamp).toLocaleString(language === 'ar' ? 'ar-SA' : 'id-ID'),
      actor: a.actor,
      type: a.type,
      detail: a.description,
      status: 'success',
    })) || [];

  const accessAreas = [
    copy.settings.areaUserLifecycle,
    copy.settings.areaFinancial,
    copy.settings.areaCurriculum,
    copy.settings.areaTerminology,
  ];

  const vocabularyPairs = [
    { from: copy.settings.vocabFromStudent, to: copy.settings.vocabToParticipant },
    { from: copy.settings.vocabFromTeacher, to: copy.settings.vocabToFacilitator },
  ];

  const previewTitle =
    previewLang === 'ar'
      ? formTitleAr || DEFAULT_BRAND_SETTINGS.titleAr
      : formTitleId || DEFAULT_BRAND_SETTINGS.titleId;
  const previewSubtitle =
    previewLang === 'ar'
      ? formSubtitleAr || DEFAULT_BRAND_SETTINGS.subtitleAr
      : formSubtitleId || DEFAULT_BRAND_SETTINGS.subtitleId;

  return (
    <div className="space-y-12 animate-fadeIn max-w-[1200px] pb-16">
      {/* Header */}
      <section>
        <div className="flex items-center gap-2 text-primary font-bold text-[10px] uppercase tracking-[0.2em] mb-2 font-label">
          <SettingsIcon size={14} />
          <Bilingual {...copy.settings.systemArchitecture} />
        </div>
        <div className="flex items-center gap-4 mb-3">
          <h1 className="text-4xl font-headline font-extrabold text-on-surface tracking-tight m-0">
            <Bilingual {...copy.settings.controlCenter} />
          </h1>
          <span className="px-2 py-0.5 bg-primary/10 text-primary text-[8px] font-bold uppercase tracking-widest rounded border border-primary/20">
            <Bilingual {...copy.settings.operationalPreview} />
          </span>
        </div>
        <p className="text-lg font-body text-on-surface-variant max-w-3xl leading-relaxed">
          <Bilingual {...copy.settings.settingsDesc} />
        </p>
      </section>

      {/* 1. Portal Identity & Branding Customization Card */}
      <Card className="border border-on-surface/5 shadow-level1 bg-surface-container-lowest p-6 sm:p-8 rounded-2xl">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-on-surface/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
              <Sliders size={20} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-headline font-bold text-on-surface m-0">
                  <Bilingual {...copy.settings.brandingSection} />
                </h3>
                <span className="px-2 py-0.5 bg-primary/10 text-primary text-[9px] font-bold uppercase tracking-widest rounded-full">
                  Real-time
                </span>
              </div>
              <p className="text-xs sm:text-sm font-body text-on-surface-variant opacity-80 mt-0.5 m-0">
                <Bilingual {...copy.settings.brandingSubtitle} />
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSaveBranding} className="mt-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Form Fields (Span 7) */}
            <div className="lg:col-span-7 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Title ID */}
                <div className="space-y-1.5">
                  <label className="text-xs font-label font-bold text-on-surface">
                    <Bilingual {...copy.settings.brandTitleIdLabel} />
                  </label>
                  <input
                    type="text"
                    value={formTitleId}
                    placeholder="Contoh: TARBIYAH PANEL"
                    onChange={e => setFormTitleId(e.target.value)}
                    className="w-full bg-surface-container-low border-none rounded-xl px-4 py-2.5 text-sm font-body text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition-all font-semibold"
                  />
                </div>

                {/* Title AR */}
                <div className="space-y-1.5">
                  <label className="text-xs font-label font-bold text-on-surface">
                    <Bilingual {...copy.settings.brandTitleArLabel} />
                  </label>
                  <input
                    type="text"
                    dir="rtl"
                    value={formTitleAr}
                    placeholder="مثال: لوحة التربية"
                    onChange={e => setFormTitleAr(e.target.value)}
                    className="w-full bg-surface-container-low border-none rounded-xl px-4 py-2.5 text-sm font-body text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition-all font-semibold"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Subtitle ID */}
                <div className="space-y-1.5">
                  <label className="text-xs font-label font-bold text-on-surface">
                    <Bilingual {...copy.settings.brandSubIdLabel} />
                  </label>
                  <input
                    type="text"
                    value={formSubtitleId}
                    placeholder="Contoh: Heritage Learning"
                    onChange={e => setFormSubtitleId(e.target.value)}
                    className="w-full bg-surface-container-low border-none rounded-xl px-4 py-2.5 text-sm font-body text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                  />
                </div>

                {/* Subtitle AR */}
                <div className="space-y-1.5">
                  <label className="text-xs font-label font-bold text-on-surface">
                    <Bilingual {...copy.settings.brandSubArLabel} />
                  </label>
                  <input
                    type="text"
                    dir="rtl"
                    value={formSubtitleAr}
                    placeholder="مثال: التعليم التراثي"
                    onChange={e => setFormSubtitleAr(e.target.value)}
                    className="w-full bg-surface-container-low border-none rounded-xl px-4 py-2.5 text-sm font-body text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3 pt-3">
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  leftIcon={<Save size={16} />}
                  className="px-5 font-bold shadow-level1"
                >
                  <Bilingual {...copy.settings.btnSaveBranding} />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="md"
                  onClick={handleResetBranding}
                  leftIcon={<RotateCcw size={15} />}
                  className="text-xs text-on-surface-variant hover:text-error hover:bg-error-container/30 transition-colors"
                >
                  <Bilingual {...copy.settings.btnResetBranding} />
                </Button>
              </div>
            </div>

            {/* Live Preview Panel (Span 5) */}
            <div className="lg:col-span-5 bg-surface-container-low p-5 rounded-2xl border border-on-surface/5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs font-label font-bold text-on-surface-variant uppercase tracking-wider">
                  <Eye size={14} className="text-primary" />
                  <Bilingual {...copy.settings.brandingPreviewTitle} />
                </div>
                {/* Language Switcher in Preview */}
                <div className="flex items-center bg-surface-container-high rounded-lg p-0.5 text-[10px] font-bold">
                  <button
                    type="button"
                    onClick={() => setPreviewLang('id')}
                    className={`px-2 py-0.5 rounded transition-all ${
                      previewLang === 'id'
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-on-surface-variant hover:text-on-surface'
                    }`}
                  >
                    ID
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewLang('ar')}
                    className={`px-2 py-0.5 rounded transition-all ${
                      previewLang === 'ar'
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-on-surface-variant hover:text-on-surface'
                    }`}
                  >
                    AR
                  </button>
                </div>
              </div>

              {/* Sidebar Header Simulation Box */}
              <div
                dir={previewLang === 'ar' ? 'rtl' : 'ltr'}
                className="bg-surface-container-lowest p-4 rounded-xl shadow-sm border border-on-surface/5 flex items-center justify-between"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center bg-surface-container-low shadow-level1 shrink-0">
                    <img
                      alt="Brand Logo"
                      className="w-full h-full object-contain p-1.5"
                      src="/logo.png"
                    />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-sm font-extrabold font-headline text-primary leading-tight uppercase tracking-tight m-0 truncate">
                      {previewTitle}
                    </h4>
                    <p className="text-[10px] text-on-surface-variant font-medium opacity-70 m-0 truncate">
                      {previewSubtitle}
                    </p>
                  </div>
                </div>

                <div className="w-7 h-7 rounded-lg bg-surface-container-high flex items-center justify-center text-on-surface-variant/70 shrink-0 opacity-60">
                  <ChevronRight size={14} className={previewLang === 'ar' ? 'rotate-180' : ''} />
                </div>
              </div>

              <p className="text-[11px] font-body text-on-surface-variant/70 m-0 leading-relaxed italic">
                * Perubahan judul dan sub-judul akan otomatis tersimpan di peramban dan langsung
                muncul di header navigasi bilah samping.
              </p>
            </div>
          </div>
        </form>
      </Card>

      {/* Mailketing transactional-email center */}
      <Card className="border border-on-surface/5">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-8">
          <div>
            <h3 className="text-xl font-headline font-bold text-on-surface m-0 flex items-center gap-3">
              <Mail className="text-primary" size={24} />
              <Bilingual {...copy.settings.emailCenterTitle} />
            </h3>
            <p className="text-sm font-body text-on-surface-variant mt-1">
              <Bilingual {...copy.settings.emailCenterDesc} />
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCheckMail}
            isLoading={isCheckingMail || isFetchingMail}
            className="flex items-center gap-2 text-xs font-bold font-label uppercase tracking-widest"
          >
            <RefreshCw
              size={14}
              className={isCheckingMail || isFetchingMail ? 'animate-spin' : ''}
            />
            <Bilingual {...copy.settings.btnCheckMail} />
          </Button>
        </div>

        {/* Disconnection Warning Alert */}
        {liveMailStatus?.status === 'DISCONNECTED' && (
          <div className="mb-6 p-4 rounded-2xl bg-error-container text-on-error-container flex items-start gap-3">
            <AlertTriangle className="shrink-0 mt-0.5" size={18} />
            <div className="text-xs space-y-1">
              <p className="font-bold m-0">
                <Bilingual {...copy.settings.mailDisconnectedTitle} />
              </p>
              <p className="m-0 opacity-90 leading-relaxed">
                {liveMailStatus?.details?.message || t(copy.settings.mailDisconnectedFallback)}
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Server Config & Live Status (Span 5) */}
          <div className="lg:col-span-5 min-w-0">
            <MailReadinessCard
              report={liveMailStatus}
              loading={isCheckingMail}
              error={mailStatusError}
            />
          </div>

          {/* Test Email Dispatch Form (Span 7) */}
          <div className="lg:col-span-7 bg-surface-container-low/50 p-6 rounded-2xl border border-on-surface/5 space-y-5">
            <h4 className="text-sm font-headline font-bold text-on-surface m-0 flex items-center gap-2">
              <Send size={16} className="text-primary" />
              Uji Coba Pengiriman Template Email
            </h4>

            <form onSubmit={handleSendTestEmail} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-label font-bold text-on-surface-variant uppercase tracking-wider mb-2">
                    <Bilingual {...copy.settings.testRecipientLabel} />
                  </label>
                  <Input
                    type="email"
                    required
                    placeholder="nama@domain.com"
                    value={testEmailTo}
                    onChange={e => setTestEmailTo(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs font-label font-bold text-on-surface-variant uppercase tracking-wider mb-2">
                    Nama Penerima (Opsional)
                  </label>
                  <Input
                    type="text"
                    placeholder="Contoh: Fulan bin Fulan"
                    value={testRecipientName}
                    onChange={e => setTestRecipientName(e.target.value)}
                    className="w-full"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-label font-bold text-on-surface-variant uppercase tracking-wider mb-2">
                  <Bilingual {...copy.settings.testTemplateLabel} />
                </label>
                <select
                  value={testTemplate}
                  onChange={e =>
                    setTestTemplate(
                      e.target.value as
                        | 'RESET_PASSWORD'
                        | 'NEW_MATERIAL'
                        | 'PENDING_EVALUATION'
                        | 'INACTIVITY_REMINDER'
                        | 'REGISTRATION_APPROVED'
                        | 'REGISTRATION_SUBMITTED'
                    )
                  }
                  className="w-full px-4 py-2.5 rounded-xl border border-outline-variant/30 bg-surface text-on-surface text-sm font-body focus:outline-hidden focus:ring-2 focus:ring-primary/20"
                >
                  <option value="REGISTRATION_SUBMITTED">
                    1. Bukti Pendaftaran Diterima & Estimasi Review (SLA 1x24 Jam)
                  </option>
                  <option value="REGISTRATION_APPROVED">
                    2. Persetujuan Pendaftaran & Undangan WhatsApp/Telegram
                  </option>
                  <option value="RESET_PASSWORD">
                    3. Pemulihan Kata Sandi (Password Reset URL)
                  </option>
                  <option value="NEW_MATERIAL">
                    4. Notifikasi Materi Pembelajaran Baru Tersedia
                  </option>
                  <option value="PENDING_EVALUATION">
                    5. Pengingat Ujian / Evaluasi Belum Diselesaikan
                  </option>
                  <option value="INACTIVITY_REMINDER">
                    6. Pengingat Santri Sudah 2 Pekan Tidak Aktif
                  </option>
                </select>
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={isSendingTestEmail}
                  className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider"
                >
                  <Send size={14} />
                  <Bilingual {...copy.settings.btnSendTestEmail} />
                </Button>
              </div>
            </form>

            {/* Last Delivery Receipt Display */}
            {lastTestResult && (
              <div
                className={`p-4 rounded-xl text-xs space-y-2 border ${
                  lastTestResult.success
                    ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-900 dark:text-emerald-200'
                    : 'bg-rose-500/10 border-rose-500/20 text-rose-900 dark:text-rose-200'
                }`}
              >
                <div className="flex items-center justify-between font-bold">
                  <span className="flex items-center gap-1.5">
                    {lastTestResult.success ? (
                      <CheckCircle size={14} className="text-emerald-600" />
                    ) : (
                      <AlertTriangle size={14} className="text-rose-600" />
                    )}
                    {lastTestResult.success ? (
                      <Bilingual {...copy.settings.testEmailAcceptedReceipt} />
                    ) : (
                      <Bilingual {...copy.settings.testEmailFailedReceipt} />
                    )}
                  </span>
                  <span className="text-[10px] font-mono opacity-70">
                    {lastTestResult.timestamp.toLocaleTimeString()}
                  </span>
                </div>
                {lastTestResult.messageId && (
                  <div className="font-mono text-[11px] bg-surface/60 p-2 rounded-lg break-all">
                    <span className="font-bold opacity-75">Message ID: </span>
                    {lastTestResult.messageId}
                  </div>
                )}
                {lastTestResult.response && (
                  <div className="font-mono text-[11px] opacity-85">
                    <span className="font-bold">Server Response: </span>
                    {lastTestResult.response}
                  </div>
                )}
                {lastTestResult.error && (
                  <div className="font-mono text-[11px] text-rose-700 dark:text-rose-300">
                    <span className="font-bold">Error: </span>
                    {lastTestResult.error}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Settings Grid */}
      <div className="grid grid-cols-12 gap-8">
        {/* Role & Permissions (Span 8) */}
        <div className="col-span-12 lg:col-span-8">
          <Card className="h-full border border-on-surface/5">
            <div className="flex justify-between items-start mb-10">
              <div className="flex items-center gap-3">
                <h3 className="text-xl font-headline font-bold text-on-surface m-0 flex items-center gap-3">
                  <ShieldAlert className="text-primary" size={24} />
                  <Bilingual {...copy.settings.accessSilos} />
                </h3>
                <span className="text-[9px] font-bold text-on-surface-variant bg-surface-container-high px-1.5 rounded uppercase tracking-tighter">
                  <Bilingual {...copy.settings.futureArchitecture} />
                </span>
              </div>
              <p className="text-sm font-body text-on-surface-variant mt-1">
                <Bilingual {...copy.settings.permissionDesc} />
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="text-[10px] uppercase tracking-widest font-bold border border-outline-variant/30"
              >
                <Bilingual {...copy.settings.btnEditSchemes} />
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-outline-variant/10">
                    <th className="pb-4 pt-2 text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant">
                      <Bilingual {...copy.settings.tableHeaderArea} />
                    </th>
                    <th className="pb-4 pt-2 text-center text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant">
                      <Bilingual {...copy.settings.roleAdmin} />
                    </th>
                    <th className="pb-4 pt-2 text-center text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant">
                      <Bilingual {...copy.settings.roleFaculty} />
                    </th>
                    <th className="pb-4 pt-2 text-center text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant">
                      <Bilingual {...copy.settings.roleAuditor} />
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/5">
                  {accessAreas.map((area, idx) => (
                    <tr key={idx} className="hover:bg-surface-container-high/30 transition-colors">
                      <td className="py-4 text-xs font-body font-bold text-on-surface">
                        <Bilingual {...area} />
                      </td>
                      <td className="py-4 text-center">
                        <CheckCircle2 size={16} className="text-primary mx-auto" />
                      </td>
                      <td className="py-4 text-center">
                        {idx === 2 ? (
                          <CheckCircle2 size={16} className="text-primary mx-auto" />
                        ) : (
                          <span className="text-outline-variant text-xs font-bold font-mono">
                            -
                          </span>
                        )}
                      </td>
                      <td className="py-4 text-center">
                        <span className="text-outline-variant text-xs font-bold font-mono">-</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* Global Vocabulary (Span 4) */}
        <div className="col-span-12 lg:col-span-4">
          <Card className="h-full border border-on-surface/5 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Languages className="text-secondary" size={24} />
                <h3 className="text-xl font-headline font-bold text-on-surface m-0">
                  <Bilingual {...copy.settings.vocabulary} />
                </h3>
              </div>
              <p className="text-sm font-body text-on-surface-variant mb-6">
                <Bilingual {...copy.settings.vocabDesc} />
              </p>

              <div className="space-y-4">
                {vocabularyPairs.map((pair, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 bg-surface-container-low rounded-xl"
                  >
                    <span className="text-xs font-body text-on-surface-variant line-through opacity-50">
                      <Bilingual {...pair.from} />
                    </span>
                    <ArrowRight size={14} className="text-outline-variant" />
                    <span className="text-xs font-body font-bold text-on-surface">
                      <Bilingual {...pair.to} />
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <Button
              variant="secondary"
              size="sm"
              className="w-full text-[10px] uppercase tracking-widest font-bold mt-6"
            >
              <Bilingual {...copy.settings.btnModifyStrings} />
            </Button>
          </Card>
        </div>

        {/* Audit Log Stream (Span 12) */}
        <div className="col-span-12">
          <Card className="border border-on-surface/5">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <History className="text-primary" size={20} />
                  <h3 className="text-xl font-headline font-bold text-on-surface m-0">
                    <Bilingual {...copy.settings.auditFeed} />
                  </h3>
                </div>
                <p className="text-sm font-body text-on-surface-variant m-0">
                  <Bilingual {...copy.settings.auditDesc} />
                </p>
              </div>
              <div className="flex items-center gap-3 w-full md:w-auto">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[10px] uppercase tracking-widest font-bold border border-outline-variant/30 flex-1 md:flex-initial"
                >
                  <Download size={14} /> <Bilingual {...copy.settings.btnExportCSV} />
                </Button>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-xs font-bold font-mono">
                  <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                  <Bilingual {...copy.settings.liveStream} />
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-outline-variant/10 text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant">
                    <th className="pb-3">
                      <Bilingual {...copy.settings.tableHeaderTimestamp} />
                    </th>
                    <th className="pb-3">
                      <Bilingual {...copy.settings.tableHeaderInitiator} />
                    </th>
                    <th className="pb-3">
                      <Bilingual {...copy.settings.tableHeaderStatus} />
                    </th>
                    <th className="pb-3">
                      <Bilingual {...copy.settings.tableHeaderDetails} />
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/5 text-xs font-body">
                  {auditLogs.length > 0 ? (
                    auditLogs.map(log => (
                      <tr
                        key={log.id}
                        className="hover:bg-surface-container-high/30 transition-colors"
                      >
                        <td className="py-3.5 font-mono text-on-surface-variant">{log.time}</td>
                        <td className="py-3.5 font-bold text-on-surface">{log.actor}</td>
                        <td className="py-3.5">
                          <span className="px-2 py-0.5 bg-surface-container-high text-on-surface-variant rounded text-[10px] font-mono font-bold">
                            {log.type}
                          </span>
                        </td>
                        <td className="py-3.5 text-on-surface-variant">{log.detail}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={4}
                        className="py-8 text-center text-on-surface-variant opacity-60"
                      >
                        Belum ada aktivitas audit log yang tercatat.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
